// ═══════════════════════════════════════════════════
// Auth Hooks — TanStack Query wrappers for auth ops
// Session verification and logout
// ═══════════════════════════════════════════════════

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import type { User } from '@/types';

// ── Query Keys ───────────────────────────────────

export const authKeys = {
  session: ['auth', 'session'] as const,
};

// ── Verify Session on App Load ───────────────────

export function useSession() {
  const accessToken = useAuthStore((s) => s.accessToken);
  const refreshToken = useAuthStore((s) => s.refreshToken);
  const setSession = useAuthStore((s) => s.setSession);
  const logout = useAuthStore((s) => s.logout);

  return useQuery({
    queryKey: authKeys.session,
    queryFn: async (): Promise<User> => {
      const data = await api.get<{ user: any; organizations: any[] }>('/auth/me');

      // Map backend response to frontend User type
      const user: User = {
        id: data.user.id,
        email: data.user.email,
        name: data.user.name,
        avatarUrl: data.user.avatar_url || null,
        role: data.organizations?.[0]?.role || 'viewer',
        organizationId: data.organizations?.[0]?.org_id || '',
        mfaEnabled: false,
        passkeysEnabled: false,
        createdAt: data.user.created_at || new Date().toISOString(),
      };

      // Update the store with verified user data
      setSession(user, accessToken!, refreshToken!);

      return user;
    },
    enabled: !!accessToken,
    retry: 1,
    staleTime: 5 * 60_000,
  });
}

// ── Logout ───────────────────────────────────────

export function useLogout() {
  const logout = useAuthStore((s) => s.logout);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async () => {
      try {
        await api.post('/auth/logout');
      } catch {
        // Even if server-side logout fails, clear locally
      }
    },
    onSettled: () => {
      logout();
      queryClient.clear();
      navigate('/login', { replace: true });
    },
  });
}
